import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "ai-chat-container" }
const _hoisted_2 = { class: "sidebar" }
const _hoisted_3 = { class: "sidebar-header" }
const _hoisted_4 = { class: "session-list" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "session-info" }
const _hoisted_7 = ["onDblclick"]
const _hoisted_8 = { class: "session-meta" }
const _hoisted_9 = { class: "message-count" }
const _hoisted_10 = { class: "session-actions" }
const _hoisted_11 = { class: "chat-panel" }
const _hoisted_12 = { class: "chat-header" }
const _hoisted_13 = {
  key: 0,
  class: "message-item ai-message"
}
const _hoisted_14 = { class: "avatar" }
const _hoisted_15 = { class: "avatar" }
const _hoisted_16 = ["src"]
const _hoisted_17 = { class: "content" }
const _hoisted_18 = ["innerHTML"]
const _hoisted_19 = {
  key: 0,
  class: "generating-indicator"
}
const _hoisted_20 = { class: "input-area" }

import { ref, nextTick, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ChatDotRound, Plus, Delete, EditPen, MoreFilled, Star, StarFilled } from '@element-plus/icons-vue';
import { getToken } from '@/user/utils/auth';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { 
  createChatSession, 
  deleteChatSession, 
  updateSessionTitle, 
  toggleSessionPin, 
  getChatSessions, 
  getSessionMessages, 
  sendChatMessage,
  ChatSessionVO,
  ChatMessageVO
} from '@/user/api/chat';

// 配置 marked 选项
interface Message {
  role: 'user' | 'assistant';
  content: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AiView',
  setup(__props) {

marked.setOptions({
  gfm: true, // 启用 GitHub 风格的 Markdown
  breaks: true, // 启用换行符转换为 <br>
  sanitize: false, // 我们使用 DOMPurify 来处理安全性，所以这里设为 false
  smartLists: true,
  smartypants: true
});

const store = useStore();
const messages = ref<Message[]>([]);
const inputMessage = ref('');
const isGenerating = ref(false);
const messagesContainerRef = ref<HTMLDivElement>();
const sessions = ref<ChatSessionVO[]>([]);
const currentSessionId = ref<number | null>(null);
const currentSessionTitle = ref('');

// 用于累积段落内容的变量
let paragraphBuffer = '';

// 获取当前用户信息
const currentUserInfo = computed(() => store.state.userInfo);

// 格式化消息内容，处理 Markdown
const formatMessageContent = (content: string) => {
  if (!content) return '';
  // 使用 marked 解析 Markdown，然后使用 DOMPurify 清理 HTML 以防止 XSS 攻击
  const parsedMd = marked.parse(content);
  return DOMPurify.sanitize(parsedMd as string);
};

// 自动滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
    }
  });
};

// 创建新会话
const createNewSession = async () => {
  try {
    const response = await createChatSession({});
    const sessionId = response.data;
    
    // 创建新会话对象
    const newSession: ChatSessionVO = {
      id: sessionId,
      title: '新对话',
      isPinned: 0,
      lastMessagePreview: '',
      messageCount: 0,
      createTime: new Date().toISOString(),
      updateTime: new Date().toISOString()
    };
    
    // 添加到会话列表顶部
    sessions.value.unshift(newSession);
    
    // 切换到新会话
    switchToSession(sessionId);
  } catch (error) {
    console.error('创建会话失败:', error);
    ElMessage.error('创建会话失败');
  }
};

// 加载会话列表
const loadSessions = async () => {
  try {
    const response = await getChatSessions();
    console.log("API Response:", response); // 调试信息
    console.log("Response type:", typeof response); // 调试信息
    console.log("Response is array?", Array.isArray(response)); // 调试信息
    
    // 检查 response 的所有属性
    console.log("Response keys:", Object.keys(response)); // 调试信息
    
    let sessionList = [];
    
    // 根据实际数据结构进行处理
    if (typeof response === 'object' && response !== null && !Array.isArray(response)) {
      // 检查可能的分页字段
      if (response.records) {
        sessionList = response.records;
        console.log("Using records field:", sessionList); // 调试信息
      } else if (response.list) {
        sessionList = response.list;
        console.log("Using list field:", sessionList); // 调试信息
      } else if (response.data) {
        // 如果响应拦截器没有自动解包 data
        if (Array.isArray(response.data)) {
          sessionList = response.data;
          console.log("Using direct data array:", sessionList); // 调试信息
        } else if (response.data.records) {
          sessionList = response.data.records;
          console.log("Using nested records field:", sessionList); // 调试信息
        } else if (response.data.list) {
          sessionList = response.data.list;
          console.log("Using nested list field:", sessionList); // 调试信息
        }
      } else {
        // 如果对象本身就是一个数组，或者检查其他可能的字段
        console.log("Unknown response structure, checking all properties"); // 调试信息
        // 尝试寻找任何可能是会话列表的属性
        for (const key in response) {
          if (Array.isArray(response[key])) {
            console.log(`Found array in property: ${key}`, response[key]); // 调试信息
            sessionList = response[key];
            break;
          }
        }
      }
    } else if (Array.isArray(response)) {
      // 直接是数组的情况
      sessionList = response;
      console.log("Direct array response:", sessionList); // 调试信息
    }
    
    // 确保sessionList是数组
    sessions.value = Array.isArray(sessionList) ? sessionList : [];
    
    console.log("Before sorting, sessions count:", sessions.value.length); // 调试信息
    
    // 按照置顶和更新时间排序
    sessions.value.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
    });
    
    console.log("Final sessions list:", sessions.value); // 调试信息
  } catch (error) {
    console.error('加载会话列表失败:', error);
    ElMessage.error('加载会话列表失败');
    // 即使出错也要确保会话数组存在
    sessions.value = [];
  }
};

// 切换到指定会话
const switchToSession = async (sessionId: number) => {
  if (sessionId === currentSessionId.value) return;

  currentSessionId.value = sessionId;
  
  // 查找当前会话标题
  const session = sessions.value.find(s => s.id === sessionId);
  if (session) {
    currentSessionTitle.value = session.title;
  }

  // 加载该会话的消息历史
  try {
    const response = await getSessionMessages(sessionId);
    const chatMessages: ChatMessageVO[] = response.data;
    
    // 转换消息格式
    messages.value = chatMessages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    
    // 滚动到底部
    nextTick(() => {
      scrollToBottom();
    });
  } catch (error) {
    console.error('加载消息历史失败:', error);
    ElMessage.error('加载消息历史失败');
  }
};

// 编辑会话标题
const editSessionTitle = (session: ChatSessionVO) => {
  ElMessageBox.prompt('请输入新的会话标题', '重命名会话', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputValue: session.title,
    inputValidator: (val) => {
      if (!val) return '标题不能为空';
      return true;
    }
  }).then(async ({ value }) => {
    try {
      await updateSessionTitle(session.id, value);
      session.title = value;
      if (session.id === currentSessionId.value) {
        currentSessionTitle.value = value;
      }
      ElMessage.success('标题更新成功');
    } catch (error) {
      console.error('更新标题失败:', error);
      ElMessage.error('更新标题失败');
    }
  }).catch(() => {
    // 取消操作
  });
};

// 会话操作
const handleSessionAction = async (command: string, session: ChatSessionVO) => {
  if (command === 'delete') {
    // 删除会话
    try {
      await deleteChatSession(session.id);
      const index = sessions.value.findIndex(s => s.id === session.id);
      if (index !== -1) {
        sessions.value.splice(index, 1);
        
        // 如果删除的是当前会话，则切换到第一个会话
        if (session.id === currentSessionId.value && sessions.value.length > 0) {
          switchToSession(sessions.value[0].id);
        } else if (session.id === currentSessionId.value && sessions.value.length === 0) {
          currentSessionId.value = null;
          messages.value = [];
          currentSessionTitle.value = '';
        }
        
        ElMessage.success('会话删除成功');
      }
    } catch (error) {
      console.error('删除会话失败:', error);
      ElMessage.error('删除会话失败');
    }
  } else if (command === 'pin') {
    // 切换置顶状态
    try {
      await toggleSessionPin(session.id);
      session.isPinned = session.isPinned ? 0 : 1;
      // 重新排序会话列表
      sessions.value.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
      });
      ElMessage.success(`已${session.isPinned ? '置顶' : '取消置顶'}会话`);
    } catch (error) {
      console.error('切换置顶状态失败:', error);
      ElMessage.error('操作失败');
    }
  } else if (command === 'rename') {
    // 重命名会话
    editSessionTitle(session);
  }
};

// 发送消息
const sendMessage = async () => {
  if (!inputMessage.value.trim()) {
    ElMessage.warning('请输入您的问题');
    return;
  }

  if (isGenerating.value) {
    ElMessage.info('AI正在回答中，请稍候...');
    return;
  }

  // 如果没有当前会话，创建一个新会话
  let sessionId = currentSessionId.value;
  if (!sessionId) {
    try {
      const response = await createChatSession({});
      sessionId = response.data;
      
      // 创建新会话对象
      const newSession: ChatSessionVO = {
        id: sessionId,
        title: '新对话',
        isPinned: 0,
        lastMessagePreview: '',
        messageCount: 0,
        createTime: new Date().toISOString(),
        updateTime: new Date().toISOString()
      };
      
      // 添加到会话列表顶部
      sessions.value.unshift(newSession);
      
      // 设置为当前会话
      currentSessionId.value = sessionId;
      currentSessionTitle.value = '新对话';
    } catch (error) {
      console.error('创建会话失败:', error);
      ElMessage.error('创建会话失败');
      return;
    }
  }

  // 添加用户消息到列表
  const userMessage: Message = {
    role: 'user',
    content: inputMessage.value
  };
  messages.value.push(userMessage);
  const userInput = inputMessage.value;
  inputMessage.value = '';

  // 添加AI消息占位符
  const aiMessage: Message = {
    role: 'assistant',
    content: ''
  };
  messages.value.push(aiMessage);
  isGenerating.value = true;

  // 滚动到底部
  scrollToBottom();

  try {
    // 由于后端使用SSE流式响应，我们需要使用fetch API手动处理
    const token = getToken();
    const baseUrl = '/api';  // 统一使用 /api 代理路径
    const url = `${baseUrl}/user/chat/message`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'token': token
      },
      body: JSON.stringify({
        sessionId: sessionId,
        content: userInput
      })
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    // 检查响应是否为流式响应
    const reader = res.body?.getReader();
    if (!reader) {
      throw new Error('Could not get response reader');
    }

    const decoder = new TextDecoder('utf-8');
    
    // 用于存储未完成的行
    let buffer = '';
    
    let cancel = false;
    while (!cancel) {
      const { done, value } = await reader.read();
      
      if (done) {
        // 处理缓冲区最后剩余的数据
        if (buffer) {
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // 如果还有段落缓冲区的内容，先添加到消息中
            if (paragraphBuffer) {
              if (lastMessage.content !== '') {
                lastMessage.content += '\n\n' + paragraphBuffer;
              } else {
                lastMessage.content = paragraphBuffer;
              }
              paragraphBuffer = '';
            }
            
            // 最后一次性添加剩余内容（可能是一部分段落内容）
            lastMessage.content += buffer;
            
            // 更新会话列表中的最后消息预览
            const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
            if (sessionIndex !== -1) {
              sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                ? lastMessage.content.substring(0, 30) + '...' 
                : lastMessage.content;
              sessions.value[sessionIndex].messageCount++;
              sessions.value[sessionIndex].updateTime = new Date().toISOString();
            }
            
            await nextTick();
            scrollToBottom();
          }
        } else if (paragraphBuffer) {
          // 即使没有buffer，如果有段落缓冲区内容也需要处理
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            if (lastMessage.content !== '') {
              lastMessage.content += '\n\n' + paragraphBuffer;
            } else {
              lastMessage.content = paragraphBuffer;
            }
            paragraphBuffer = '';
            
            // 更新会话列表中的最后消息预览
            const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
            if (sessionIndex !== -1) {
              sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                ? lastMessage.content.substring(0, 30) + '...' 
                : lastMessage.content;
              sessions.value[sessionIndex].messageCount++;
              sessions.value[sessionIndex].updateTime = new Date().toISOString();
            }
            
            await nextTick();
            scrollToBottom();
          }
        }
        cancel = true;
        break;
      }

      // 解码接收到的数据
      const chunk = decoder.decode(value, { stream: true });
      
      // 按行分割数据
      const lines = chunk.split('\n');
      
      // 处理第一行，合并到缓冲区（如果之前有未处理完的内容）
      lines[0] = buffer + lines[0];
      
      // 更新缓冲区为最后一行（可能未完成的行）
      buffer = lines[lines.length - 1];
      
      // 处理完整的行（除了最后一行，因为它是可能未完成的）
      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        
        // 检查是否是SSE数据行 (以 "data:" 开头)
        if (line.startsWith('data:')) {
          // 提取数据部分
          const data = line.substring(5).trim(); // 移除 "data:" 前缀
          
          // 获取AI消息对象
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // 如果不是空数据，添加到段落缓冲区
            if (data !== '') {
              // 避免在段落缓冲区开始处添加多余的换行
              if (paragraphBuffer === '') {
                paragraphBuffer = data;
              } else {
                // 在段落缓冲区内，用单个换行连接连续的数据块
                paragraphBuffer += '\n' + data;
              }
            } else {
              // 如果是空的data行，表示一个段落结束，将段落缓冲区的内容添加到消息中
              if (paragraphBuffer) {
                // 如果当前内容不为空，需要添加双换行符后再添加新段落
                if (lastMessage.content !== '') {
                  lastMessage.content += '\n\n' + paragraphBuffer;
                } else {
                  lastMessage.content = paragraphBuffer;
                }
                
                // 清空段落缓冲区
                paragraphBuffer = '';
                
                // 强制更新DOM，确保及时显示并滚动到底部
                await nextTick();
                scrollToBottom();
                
                // 更新会话列表中的最后消息预览
                const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
                if (sessionIndex !== -1) {
                  // 实时更新预览内容，保持最新状态
                  sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                    ? lastMessage.content.substring(0, 30) + '...' 
                    : lastMessage.content;
                }
              }
            }
          }
        }
      }
    }

    // 完成后设置生成状态为false
    isGenerating.value = false;
  } catch (error) {
    console.error('发送消息失败:', error);
    ElMessage.error('AI回答出现问题，请稍后重试');
    
    isGenerating.value = false;
    
    // 移除AI消息占位符
    const lastMessage = messages.value[messages.value.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      messages.value.pop();
    }
  }
};

// 处理发送快捷键
const handleSendWithKeydown = (event: KeyboardEvent) => {
  if (event.shiftKey) return; // 如果按下Shift键，不触发发送
  event.preventDefault(); // 阻止换行
  if (!event.isComposing && event.key === 'Enter') {
    sendMessage();
  }
};

// 初始化加载会话列表
onMounted(() => {
  loadSessions();
});

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_button, {
          type: "primary",
          size: "default",
          onClick: createNewSession,
          icon: _unref(Plus),
          class: "new-session-btn"
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
            _createTextVNode(" 新建对话 ", -1)
          ]))]),
          _: 1
        }, 8, ["icon"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sessions.value, (session) => {
          return (_openBlock(), _createElementBlock("div", {
            key: session.id,
            class: _normalizeClass(["session-item", { active: session.id === currentSessionId.value }]),
            onClick: ($event: any) => (switchToSession(session.id))
          }, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", {
                class: "session-title",
                onDblclick: ($event: any) => (editSessionTitle(session))
              }, _toDisplayString(session.title), 41, _hoisted_7),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("span", _hoisted_9, _toDisplayString(session.messageCount) + " 条消息", 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_component_el_dropdown, {
                trigger: "click",
                onCommand: ($event: any) => (handleSessionAction($event, session))
              }, {
                dropdown: _withCtx(() => [
                  _createVNode(_component_el_dropdown_menu, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_dropdown_item, {
                        command: "pin",
                        icon: session.isPinned ? _unref(StarFilled) : _unref(Star)
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(session.isPinned ? '取消置顶' : '置顶'), 1)
                        ]),
                        _: 2
                      }, 1032, ["icon"]),
                      _createVNode(_component_el_dropdown_item, {
                        command: "rename",
                        icon: _unref(EditPen)
                      }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode("重命名", -1)
                        ]))]),
                        _: 1
                      }, 8, ["icon"]),
                      _createVNode(_component_el_dropdown_item, {
                        command: "delete",
                        icon: _unref(Delete),
                        divided: ""
                      }, {
                        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                          _createTextVNode("删除", -1)
                        ]))]),
                        _: 1
                      }, 8, ["icon"])
                    ]),
                    _: 2
                  }, 1024)
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_el_button, {
                    type: "default",
                    size: "small",
                    icon: "MoreFilled",
                    circle: ""
                  })
                ]),
                _: 2
              }, 1032, ["onCommand"])
            ])
          ], 10, _hoisted_5))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("h3", null, _toDisplayString(currentSessionTitle.value || '🤖 钩织小助手'), 1),
        _cache[4] || (_cache[4] = _createElementVNode("p", null, "我可以帮助您解答钩织技巧、图案设计、材料选择等相关问题", -1))
      ]),
      _createElementVNode("div", {
        class: "chat-messages",
        ref_key: "messagesContainerRef",
        ref: messagesContainerRef
      }, [
        (messages.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_component_el_icon, { class: "avatar-icon" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ChatDotRound))
                  ]),
                  _: 1
                })
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "content" }, [
                _createElementVNode("div", { class: "message-content" }, [
                  _createElementVNode("p", null, "您好！我是钩织小助手，有什么可以帮助您的吗？"),
                  _createElementVNode("p", null, "您可以问我关于钩织技巧、图案设计、材料选择等问题。")
                ])
              ], -1))
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (message, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: _normalizeClass(["message-item", message.role === 'assistant' ? 'ai-message' : 'user-message'])
          }, [
            _createElementVNode("div", _hoisted_15, [
              (message.role === 'assistant')
                ? (_openBlock(), _createBlock(_component_el_icon, {
                    key: 0,
                    class: "avatar-icon"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(ChatDotRound))
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: currentUserInfo.value?.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                    alt: "User Avatar",
                    class: "avatar-img"
                  }, null, 8, _hoisted_16))
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", {
                class: _normalizeClass(["message-content", { typing: message.role === 'assistant' && isGenerating.value && index === messages.value.length - 1 }]),
                innerHTML: formatMessageContent(message.content)
              }, null, 10, _hoisted_18),
              (message.role === 'assistant' && isGenerating.value && index === messages.value.length - 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("span", { class: "loading-spinner" }, null, -1),
                    _createElementVNode("span", { class: "generating-text" }, "生成中", -1)
                  ]))]))
                : _createCommentVNode("", true)
            ])
          ], 2))
        }), 128))
      ], 512),
      _createElementVNode("div", _hoisted_20, [
        _createVNode(_component_el_input, {
          modelValue: inputMessage.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputMessage).value = $event)),
          type: "textarea",
          rows: 3,
          placeholder: "输入您想问的问题...",
          maxlength: "500",
          "show-word-limit": "",
          disabled: isGenerating.value,
          onKeydown: _withKeys(handleSendWithKeydown, ["enter"])
        }, null, 8, ["modelValue", "disabled"]),
        _createVNode(_component_el_button, {
          type: "primary",
          disabled: !inputMessage.value.trim() || isGenerating.value,
          onClick: sendMessage,
          class: "send-btn"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(isGenerating.value ? '生成中...' : '发送'), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ])
  ]))
}
}

})